// Сетка слотов доставки инстанса — с бэкенда (GET /settings, env TIME_SLOTS,
// обязателен — бэк без него не стартует). Никаких зашитых сеток-заглушек:
// пока конфиг не загружен, список пуст; неудачная загрузка не кэшируется.
// Для фильтров по существующим заказам использовать unionWithOrderSlots:
// старые заказы могут ехать по слотам, которых уже нет в актуальной сетке
// (grandfathering).
import { useEffect, useState } from 'react';
import { api } from './api';

const initial = {
  timeSlots: [],
  leadTimeMinutes: 120,
  shopTimezone: 'Europe/Moscow',
  loaded: false,
};

/** Сортировка по слоту: слоты по строке, пустое (время согласовать) — в конце */
export function compareBySlot(a, b) {
  const ta = a.delivery_time || '';
  const tb = b.delivery_time || '';
  if (ta === tb) return 0;
  if (!ta) return 1;
  if (!tb) return -1;
  return ta.localeCompare(tb);
}

/** Внутри одного слота: «срочно» (Flowwow «ближайшее время», order.is_asap) первыми — между
 *  собой по времени заказа; дальше заказы с точным временем готовности (Flowwow: «готов к
 *  HH:MM» в deliver_from), по возрастанию; без времени — после. */
export function compareByReadyTime(a, b) {
  const aa = a.is_asap ? 0 : 1;
  const ab = b.is_asap ? 0 : 1;
  if (aa !== ab) return aa - ab;
  if (a.is_asap && b.is_asap) {
    return String(a.order_date || '').localeCompare(String(b.order_date || ''));
  }
  const ra = a.deliver_from || '';
  const rb = b.deliver_from || '';
  if (ra === rb) return 0;
  if (!ra) return 1;
  if (!rb) return -1;
  return ra.localeCompare(rb);
}

/** Минуты от полуночи до начала слота ("13:00-15:00" → 780) */
export function slotStartMinutes(slot) {
  return parseInt(slot.slice(0, 2), 10) * 60 + parseInt(slot.slice(3, 5), 10);
}

/** Колонки сетки слотов в формах заказа — ряды ровные, без «повисших» слотов (владелец 26.10):
 *  до 5 — одним рядом, дальше 4 / 3 / 5, что делит без остатка (6 → 3 + 3, 8 → 4 + 4), иначе 4 */
export function slotGridCols(n) {
  if (n <= 5) return Math.max(n, 1);
  return [4, 3, 5].find(c => n % c === 0) || 4;
}

/** Актуальная сетка + слоты, реально встречающиеся в заказах, по началу */
export function unionWithOrderSlots(timeSlots, orders) {
  const set = new Set(timeSlots);
  (orders || []).forEach(o => {
    if (o && o.delivery_time && /^\d{2}:\d{2}-\d{2}:\d{2}$/.test(o.delivery_time)) set.add(o.delivery_time);
  });
  return [...set].sort((a, b) => slotStartMinutes(a) - slotStartMinutes(b));
}

let cache = null;
let pending = null;

function fetchDeliverySlots() {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = api.getSettings()
      .then((s) => {
        cache = {
          timeSlots: Array.isArray(s.time_slots) ? s.time_slots : [],
          leadTimeMinutes: Number.isFinite(s.order_lead_time_minutes)
            ? s.order_lead_time_minutes : initial.leadTimeMinutes,
          shopTimezone: s.shop_timezone || initial.shopTimezone,
          loaded: true,
        };
        return cache;
      })
      .catch(() => {
        pending = null;
        return initial;
      });
  }
  return pending;
}

export function useDeliverySlots() {
  const [config, setConfig] = useState(cache || initial);
  useEffect(() => {
    let alive = true;
    fetchDeliverySlots().then((c) => { if (alive) setConfig(c); });
    return () => { alive = false; };
  }, []);
  return config;
}
